import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import EditAgentBlueprintForm from "@/components/EditAgentBlueprintForm";
import { getAgentBlueprints } from "@/lib/formOptions";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/agent-blueprints/$id/")({
  component: EditAgentBlueprintPage,
});

function EditAgentBlueprintPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [blueprint, setBlueprint] = useState<FormOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    getAgentBlueprints()
      .then((options) => {
        if (!active) return;
        const found = options.find((option) => option.id === id);
        if (!found) {
          setNotFound(true);
        } else {
          setBlueprint(found);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching blueprint:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const goToList = () => navigate({ to: "/dashboard/agent-blueprints" });

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !blueprint) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Blueprint not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EntityFormPage
      title="Edit Blueprint"
      description={`Update ${blueprint.label}'s defaults.`}
      backHref="/dashboard/agent-blueprints"
      headerActions={
        !showDeleteConfirm ? (
          <Button type="button" variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        ) : undefined
      }
    >
      <EditAgentBlueprintForm
        blueprint={blueprint}
        showDeleteConfirm={showDeleteConfirm}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onDeleted={goToList}
        onSaved={goToList}
      />
    </EntityFormPage>
  );
}
